<template>
  <div>
    <Manpowermenubar_govem />
    <div class="government-employee-container">

    
    <!-- Header Section with Gradient -->
    <v-card class="mb-4 elevation-3 header-card">
      <v-card-title class="bg-gradient white--text py-5">
        <v-icon class="mr-3" size="32">mdi-account-group</v-icon>
        <div>
          <div class=" font-weight-bold">
            บริหารจัดการอัตรากำลังพนักงานราชการ
          </div>
          <div class=" mt-2 opacity-8">
            สำนักงานคณะกรรมการการอาชีวศึกษา
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- Summary Statistics Cards with Animations -->
    <v-row class="mb-4">
      <v-col
        cols="12"
        sm="6"
        md="3"
        v-for="(stat, index) in summaryCardsData"
        :key="index"
      >
        <v-card
          class="text-center elevation-1 summary-card summary-card-hover"
          :class="`delay-${index}`"
        >
          <v-card-text>
            <v-icon :color="stat.color" class="mb-2" size="28">{{
              stat.icon
            }}</v-icon>
            <div class=" font-weight-bold" :class="`text-${stat.color}`">
              {{ stat.value }}
            </div>
            <div class=" gray--text">{{ stat.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Additional Stats Row -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="4">
        <v-card class="text-center elevation-1 stat-card">
          <v-card-text>
            <v-icon color="error" class="mb-2">mdi-arrow-bottom</v-icon>
            <div class=" font-weight-bold text-error">
              {{ summaryStats.min_salary | currency }}
            </div>
            <div class=" gray--text">เงินเดือนต่ำสุด</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-card class="text-center elevation-1 stat-card">
          <v-card-text>
            <v-icon color="success" class="mb-2">mdi-arrow-top</v-icon>
            <div class=" font-weight-bold text-success">
              {{ summaryStats.max_salary | currency }}
            </div>
            <div class=" gray--text">เงินเดือนสูงสุด</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-card class="text-center elevation-1 stat-card">
          <v-card-text>
            <v-icon color="info" class="mb-2">mdi-card-account-details</v-icon>
            <div class=" font-weight-bold text-info">
              {{ summaryStats.with_idcard_count }}
            </div>
            <div class=" gray--text">มีเลขบัตรประชาชน</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section - Row 1 -->
    <v-row class="mb-4">
      <!-- Gender Distribution Chart -->
      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100 chart-card">
          <v-card-title class="bg-gradient-blue white--text">
            <v-icon class="mr-2">mdi-chart-pie</v-icon>
            สัดส่วนเพศ
          </v-card-title>
          <v-card-text
            class="chart-container"
            v-if="genderChartSeries.length > 0"
          >
            <div class="chart-wrapper">
              <apexchart
                type="donut"
                :options="genderChartOptions"
                :series="genderChartSeries"
                height="350"
              />
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="text-center">
              <v-chip
                v-for="(value, index) in summaryStats.male_count
                  ? [
                      {
                        text: 'ชาย',
                        count: summaryStats.male_count,
                        color: 'blue'
                      },
                      {
                        text: 'หญิง',
                        count: summaryStats.female_count,
                        color: 'pink'
                      }
                    ]
                  : []"
                :key="index"
                :color="value.color"
                text-color="white"
                class="mr-2 mb-2"
              >
                {{ value.text }}: {{ value.count }} คน
              </v-chip>
            </div>
          </v-card-text>
          <v-card-text v-else class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Salary Range Distribution Chart -->
      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100 chart-card">
          <v-card-title class="bg-gradient-orange white--text">
            <v-icon class="mr-2">mdi-chart-bar</v-icon>
            สถิติเงินเดือนตามช่วง
          </v-card-title>
          <v-card-text
            class="chart-container"
            v-if="salaryRangeChartSeries.length > 0"
          >
            <apexchart
              type="bar"
              :options="salaryRangeChartOptions"
              :series="salaryRangeChartSeries"
              height="350"
            />
          </v-card-text>
          <v-card-text v-else class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section - Row 2 -->
    <v-row class="mb-4">
      <!-- Position Distribution Chart -->
      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100 chart-card">
          <v-card-title class="bg-gradient-green white--text">
            <v-icon class="mr-2">mdi-chart-line</v-icon>
            จำนวนตำแหน่งแรกละเอียด
          </v-card-title>
          <v-card-text
            class="chart-container"
            v-if="positionChartSeries.length > 0"
          >
            <apexchart
              type="bar"
              :options="positionChartOptions"
              :series="positionChartSeries"
              height="350"
            />
          </v-card-text>
          <v-card-text v-else class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Education Level Chart -->
      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100 chart-card">
          <v-card-title class="bg-gradient-purple white--text">
            <v-icon class="mr-2">mdi-chart-column</v-icon>
            สถิติการศึกษา
          </v-card-title>
          <v-card-text
            class="chart-container"
            v-if="educationChartSeries.length > 0"
          >
            <apexchart
              type="bar"
              :options="educationChartOptions"
              :series="educationChartSeries"
              height="400"
              :key="educationChartKey"
            />
          </v-card-text>
          <v-card-text v-else class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- College Summary Table -->
    <v-card class="mb-4 elevation-2">
      <v-card-title class="bg-secondary white--text">
        <v-icon class="mr-2">mdi-building</v-icon>
        สรุปตามสถานศึกษา <v-spacer></v-spacer>
        <v-btn color="info" class="mb-2" @click="exportSummaryToCSV">
          <v-icon class="mr-2">mdi-download</v-icon>
          ส่งออก CSV
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="collegeSummaryHeaders"
          :items="collegeSummary"
          :loading="loading"
          class="elevation-1 table-hover"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageOptions: [10, 15, 20],
            itemsPerPageText: 'แสดงรายการต่อหน้า:'
          }"
          :search="search_college"
        >
        <template v-slot:top>
          <v-text-field 
            v-model="search_college"
            label="ค้นหาตามสถานศึกษา"
            prepend-icon="mdi-magnify"
            clearable
            outlined
            dense
          ></v-text-field>
        </template>


          <template v-slot:item.total_salary="{ item }">
            <span class="font-weight-bold text-success">{{
              item.total_salary | currency
            }}</span>
          </template>
          <template v-slot:item.avg_salary="{ item }">
            <span class="font-weight-bold text-info">{{
              item.avg_salary | currency
            }}</span>
          </template>
          <template v-slot:item.male_count="{ item }">
            <v-chip color="blue" text-color="white" small>{{
              item.male_count
            }}</v-chip>
          </template>
          <template v-slot:item.female_count="{ item }">
            <v-chip color="pink" text-color="white" small>{{
              item.female_count
            }}</v-chip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Search and Filter Section -->
    <v-card class="mb-4 elevation-2">
      <v-card-title class="bg-secondary white--text">
        <v-icon class="mr-2">mdi-magnify</v-icon>
        ค้นหาและคัดกรอง
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="search"
              label="ค้นหาตามเลขที่ตำแหน่ง"
              placeholder="กรอกเลขที่ตำแหน่ง"
              prepend-icon="mdi-magnify"
              @keyup.enter="idPosition_search"
              clearable
              outlined
              dense
              rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="college_search"
              :items="colleges"
              item-text="user_firstname"
              item-value="user_code"
              label="ค้นหาตามวิทยาลัย"
              prepend-icon="mdi-home-city"
              clearable
              outlined
              dense
              rounded
              @change="onFilterChange"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="province_search"
              :items="provinces"
              item-text="province_name"
              item-value="province_ID"
              label="ค้นหาตามจังหวัด"
              prepend-icon="mdi-map-marker"
              clearable
              outlined
              dense
              rounded
              @change="onFilterChange"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12">
            <div class="button-group">
              <v-btn
                color="primary"
                class="mr-2 mb-2"
                @click="government_employeeQuerynull"
                outlined
              >
                <v-icon class="mr-2">mdi-filter-off</v-icon>
                แสดงข้อมูลไม่มีเลขบัตรประชาชน
              </v-btn>
              <v-btn
                color="secondary"
                class="mr-2 mb-2"
                @click="government_employeeQueryAll"
                outlined
              >
                <v-icon class="mr-2">mdi-refresh</v-icon>
                แสดงทั้งหมด
              </v-btn>
              <v-btn
                color="success"
                class="mr-2 mb-2"
                @click="government_employeeAdd"
              >
                <v-icon class="mr-2">mdi-plus</v-icon>
                เพิ่มข้อมูลใหม่
              </v-btn>
              <v-btn color="info" class="mb-2" @click="exportToCSV">
                <v-icon class="mr-2">mdi-download</v-icon>
                ส่งออก CSV
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table Section -->
    <v-card elevation="2">
      <v-card-title class="bg-gray white--text">
        <v-icon class="mr-2">mdi-table</v-icon>
        รายชื่ออัตรากำลังพนักงานราชการ
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="government_employees"
          :loading="loading"
          class="elevation-1 table-hover"
          :items-per-page="15"
          :footer-props="{
            itemsPerPageOptions: [10, 15, 20, 50],
            itemsPerPageText: 'แสดงรายการต่อหน้า:'
          }"
          :search="search"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn
              small
              color="primary"
              class="mr-1"
              @click="government_employeeEdit(item.id_no)"
              outlined
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              small
              color="error"
              @click="government_employeeDelete(item.id_no)"
              outlined
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.salary_s="{ item }">
            <span class="font-weight-bold text-success">{{
              item.salary_s | currency
            }}</span>
          </template>

          <template v-slot:item.sex="{ item }">
            <v-chip
              small
              :color="item.sex === 'ชาย' ? 'blue' : 'pink'"
              text-color="white"
            >
              <v-icon small class="mr-1">{{
                item.sex === "ชาย" ? "mdi-human-male" : "mdi-human-female"
              }}</v-icon>
              {{ item.sex }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon large class="gray--text">mdi-folder-open</v-icon>
              <p class="gray--text">ไม่มีข้อมูลในขณะนี้</p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog
      v-model="addgovernment_employeedialog"
      max-width="900px"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="bg-success white--text sticky-top">
          <v-icon class="mr-2">mdi-plus</v-icon>
          เพิ่มข้อมูลพนักงานราชการใหม่
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            color="white"
            @click="addgovernment_employeedialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-form ref="addgovernment_employeeform" lazy-validation>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="addgovernment_employee.id_position"
                  label="เลขที่ตำแหน่ง*"
                  required
                  :rules="[v => !!v || 'กรุณากรอกข้อมูล']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="addgovernment_employee.college_code"
                  :items="colleges"
                  item-text="user_name"
                  item-value="user_code"
                  label="วิทยาลัย*"
                  required
                  :rules="[v => !!v || 'กรุณาเลือกวิทยาลัย']"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="addgovernment_employee.id_card"
                  label="เลขบัตรประชาชน"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="addgovernment_employee.title_s"
                  :items="titles"
                  label="คำนำหน้า"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="addgovernment_employee.frist_name"
                  label="ชื่อ"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="addgovernment_employee.last_name"
                  label="นามสกุล"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="addgovernment_employee.sex"
                  :items="['ชาย', 'หญิง']"
                  label="เพศ"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="addgovernment_employee.salary_s"
                  label="เงินเดือน"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="addgovernment_employee.name_postion"
                  label="ชื่อตำแหน่ง"
                  :items="positionNames"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="gray"
            @click="addgovernment_employeedialog = false"
            text
          >
            <v-icon class="mr-2">mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn color="success" @click="addgovernment_employeeSubmit">
            <v-icon class="mr-2">mdi-check</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog
      v-model="editgovernment_employeedialog"
      max-width="900px"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="bg-primary white--text sticky-top">
          <v-icon class="mr-2">mdi-pencil</v-icon>
          แก้ไขข้อมูลพนักงานราชการ
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            color="white"
            @click="editgovernment_employeedialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="info" prominent class="mb-4">
            <v-icon class="mr-2">mdi-information</v-icon>
            {{ fullname }}
          </v-alert>
          <v-form ref="editgovernment_employeeform" lazy-validation>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editgovernment_employee.id_position"
                  label="เลขที่ตำแหน่ง"
                  disabled
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="editgovernment_employee.college_code"
                  :items="colleges"
                  item-text="user_name"
                  item-value="user_code"
                  label="วิทยาลัย"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editgovernment_employee.id_card"
                  label="เลขบัตรประชาชน"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="editgovernment_employee.title_s"
                  :items="titles"
                  label="คำนำหน้า"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editgovernment_employee.frist_name"
                  label="ชื่อ"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="editgovernment_employee.last_name"
                  label="นามสกุล"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="editgovernment_employee.sex"
                  :items="['ชาย', 'หญิง']"
                  label="เพศ"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="editgovernment_employee.salary_s"
                  label="เงินเดือน"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="editgovernment_employee.name_postion"
                  label="ชื่อตำแหน่ง"
                  :items="positionNames"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4 bg-light-gray">
          <v-spacer></v-spacer>
          <v-btn
            color="gray"
            @click="editgovernment_employeedialog = false"
            text
          >
            <v-icon class="mr-2">mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn color="warning" @click="clearIdcard" text class="mr-2">
            <v-icon class="mr-2">mdi-delete</v-icon>
            ลบเลขบัตร
          </v-btn>
          <v-btn color="primary" @click="editgovernment_employeeSubmit">
            <v-icon class="mr-2">mdi-check</v-icon>
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog
      v-model="deletegovernment_employeedialog"
      max-width="600px"
      persistent
    >
      <v-card>
        <v-card-title class="bg-error white--text">
          <v-icon class="mr-2">mdi-alert</v-icon>
          ยืนยันการลบข้อมูล
        </v-card-title>
        <v-card-text class="py-4">
          <v-alert type="warning" prominent icon="mdi-alert-circle">
            <div class="">
              คุณต้องการลบข้อมูลของ
              <strong
                >{{ editgovernment_employee.title_s
                }}{{ editgovernment_employee.frist_name }}
                {{ editgovernment_employee.last_name }}</strong
              >
              หรือไม่?
            </div>
            <div class=" mt-2">
              เลขที่ตำแหน่ง: {{ editgovernment_employee.id_position }}
            </div>
          </v-alert>
          <v-form
            ref="deletegovernment_employeeform"
            lazy-validation
            class="mt-4"
          >
            <v-text-field
              v-model="deleteConfirm"
              label="กรุณาพิมพ์ 'ยืนยันการลบ' เพื่อยืนยัน"
              :rules="[v => v === 'ยืนยันการลบ' || 'ข้อมูลไม่ตรงกัน']"
              outlined
              placeholder="ยืนยันการลบ"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="gray"
            @click="deletegovernment_employeedialog = false"
            text
          >
            <v-icon class="mr-2">mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn color="error" @click="deletegovernment_employeeSubmit">
            <v-icon class="mr-2">mdi-delete</v-icon>
            ลบข้อมูล
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay v-model="loading" opacity="0.5">
      <v-progress-circular
        indeterminate
        size="50"
        color="primary"
      ></v-progress-circular>
    </v-overlay>
  </div>
  </div>
</template>

<script>
import Manpowermenubar_govem from "../../../components/admin/manpowermenubar_govem.vue";

export default {
  components: { Manpowermenubar_govem },
  data() {
    return {
      loading: true,
      ApiKey: "HRvec2021",
      government_employees: [],
      colleges: [],
      provinces: [],
      regions: [],
      search: "",
      search_college: "",
      college_search: "",
      province_search: "",
      addgovernment_employeedialog: false,
      editgovernment_employeedialog: false,
      deletegovernment_employeedialog: false,
      addgovernment_employee: {},
      editgovernment_employee: {},
      delIppostion: {},
      fullname: "",
      deleteConfirm: "",
      data_syslog: {},
      date_today_log: new Date().toISOString().split("T")[0],

      // Summary data
      summaryStats: {
        total_employees: 0,
        total_salary: 0,
        avg_salary: 0,
        min_salary: 0,
        max_salary: 0,
        male_count: 0,
        female_count: 0,
        no_idcard_count: 0,
        with_idcard_count: 0
      },
      collegeSummary: [],

      titles: ["นาย", "นาง", "นางสาว", "ว่าที่ร้อยตรี", "ด.ส."],
      positionNames: [
        "พนักงานบริหารทั่วไป (ครู)",
        "พนักงานสนับสนุนการศึกษา",
        "ครูผู้ช่วย",
        "ครูผู้สอน",
        "ผู้บริหาร"
      ],

      collegeSummaryHeaders: [
        { text: "รหัสสถานศึกษา", value: "user_code", width: "150px" },
        { text: "สถานศึกษา", value: "user_firstname", width: "250px" },
        { text: "จำนวนคน", value: "total_employees", width: "100px" },
        { text: "เงินเดือนรวม", value: "total_salary", width: "130px" },
        { text: "เงินเดือนเฉลี่ย", value: "avg_salary", width: "130px" },
        { text: "ชาย", value: "male_count", width: "80px" },
        { text: "หญิง", value: "female_count", width: "80px" }
      ],

      headers: [
        { text: "เลขที่ตำแหน่ง", value: "id_position", width: "100px" },
        { text: "เลขบัตรประชาชน", value: "id_card", width: "140px" },
        { text: "ชื่อ - สกุล", value: "fname", width: "200px" },
        { text: "ตำแหน่ง", value: "name_postion", width: "200px" },
        { text: "วิทยาลัย", value: "user_name", width: "180px" },
        { text: "วิทยาลัย", value: "user_firstname", width: "180px" },
        { text: "เพศ", value: "sex", width: "80px" },
        { text: "เงินเดือน", value: "salary_s", width: "100px" },
        { text: "การศึกษา", value: "ed_name", width: "150px" },
        { text: "จังหวัด", value: "province_name", width: "100px" },
        {
          text: "การดำเนินการ",
          value: "actions",
          width: "100px",
          sortable: false
        }
      ],

      // Chart Options - เพิ่มความสวยงาม
      genderChartOptions: {
        chart: {
          type: "donut",
          sparkline: { enabled: false },
          animations: {
            enabled: true,
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            }
          }
        },
        labels: ["ชาย", "หญิง"],
        colors: ["#2196F3", "#E91E63"],
        legend: {
          position: "bottom",
          fontSize: "14px"
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return Math.round(val) + "%";
          },
          style: {
            fontSize: "12px",
            fontWeight: "bold"
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%"
            }
          }
        },
        states: {
          hover: {
            filter: {
              type: "darken",
              value: 0.15
            }
          }
        }
      },
      genderChartSeries: [],

      salaryRangeChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: true },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#FF9800"],
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        yaxis: {
          title: {
            text: "จำนวนคน"
          }
        },
        grid: {
          borderColor: "#e0e0e0"
        },
        states: {
          hover: {
            filter: {
              type: "darken",
              value: 0.15
            }
          }
        }
      },
      salaryRangeChartSeries: [],

      positionChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: true },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#4CAF50"],
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        yaxis: {
          title: {
            text: "จำนวนคน"
          }
        },
        grid: {
          borderColor: "#e0e0e0"
        },
        states: {
          hover: {
            filter: {
              type: "darken",
              value: 0.15
            }
          }
        }
      },
      positionChartSeries: [],

      educationChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: true },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#9C27B0"],
        xaxis: {
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        plotOptions: {
          bar: {
            horizontal: true, // ถ้าใช้ type: 'bar' ต้องเปิดตรงนี้
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: "11px"
          }
        },
        yaxis: {
          title: {
            text: "จำนวนคน"
          }
        },
        grid: {
          borderColor: "#e0e0e0"
        },
        states: {
          hover: {
            filter: {
              type: "darken",
              value: 0.15
            }
          }
        }
      },
      educationChartSeries: [],
      educationChartKey: 0
    };
  },

  computed: {
    summaryCardsData() {
      return [
        {
          value: this.summaryStats.total_employees,
          label: "รวมอัตรากำลัง",
          icon: "mdi-account-multiple",
          color: "primary"
        },
        {
          value: this.$options.filters.currency(this.summaryStats.total_salary),
          label: "เงินเดือนรวม",
          icon: "mdi-cash-multiple",
          color: "success"
        },
        {
          value: this.$options.filters.currency(this.summaryStats.avg_salary),
          label: "เงินเดือนเฉลี่ย",
          icon: "mdi-cash",
          color: "info"
        },
        {
          value: `${this.summaryStats.male_count} / ${this.summaryStats.female_count}`,
          label: "ชาย / หญิง",
          icon: "mdi-gender-male-female",
          color: "warning"
        }
      ];
    }
  },

  filters: {
    currency(value) {
      if (!value) return "0.00";
      return new Intl.NumberFormat("th-TH", {
        style: "currency",
        currency: "THB",
        minimumFractionDigits: 2
      }).format(value);
    }
  },

  async mounted() {
    await this.loadInitialData();
  },

  methods: {
    async loadInitialData() {
      try {
        this.loading = true;

        // Load Master Data
        let result = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          show_user: "ok"
        });
        this.colleges = result.data || [];

        result = await this.$http.post("province.php", {
          ApiKey: this.ApiKey
        });
        this.provinces = result.data || [];

        result = await this.$http.post("region.php", {
          ApiKey: this.ApiKey
        });
        this.regions = result.data || [];

        // Load employee data and stats
        await this.loadEmployeeData();
        await this.loadStats();
      } catch (error) {
        console.error("Error loading initial data:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลได้",
          timer: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    async loadEmployeeData() {
      try {
        const payload = {
          ApiKey: this.ApiKey
        };

        if (this.college_search) {
          payload.college_code = this.college_search;
        }

        if (this.province_search) {
          payload.province_ID = this.province_search;
        }

        const result = await this.$http.post(
          "government_employee_news.php",
          payload
        );
        this.government_employees = result.data.data || [];
      } catch (error) {
        console.error("Error loading employee data:", error);
      }
    },

    async loadStats() {
      try {
        const payload = {
          ApiKey: this.ApiKey
        };

        if (this.college_search) {
          payload.college_code = this.college_search;
        }

        if (this.province_search) {
          payload.province_ID = this.province_search;
        }

        const result = await this.$http.post(
          "government_employee_stats.php",
          payload
        );

        if (result.data.status) {
          this.summaryStats = result.data.summary || {};
          this.collegeSummary = result.data.college_summary || [];
          this.updateCharts(result.data);
        }
      } catch (error) {
        console.error("Error loading stats:", error);
      }
    },

    updateCharts(statsData) {
      // Update Gender Chart - ตรวจสอบ data ก่อน
      const maleCount = parseInt(this.summaryStats.male_count) || 0;
      const femaleCount = parseInt(this.summaryStats.female_count) || 0;

      if (maleCount > 0 || femaleCount > 0) {
        this.genderChartSeries = [maleCount, femaleCount];
      }

      // Update Salary Range Chart
      const salaryRanges = statsData.salary_ranges || [];
      if (salaryRanges.length > 0) {
        this.salaryRangeChartOptions.xaxis.categories = salaryRanges.map(
          r => r.salary_range
        );
        this.salaryRangeChartSeries = [
          {
            name: "จำนวนคน",
            data: salaryRanges.map(r => {
              const val = parseInt(r.count);
              return isNaN(val) ? 0 : val;
            })
          }
        ];
      }

      // Update Position Chart
      const positions = (statsData.position_distribution || []).slice(0, 10);
      if (positions.length > 0) {
        this.positionChartOptions.xaxis.categories = positions.map(
          p =>
            (p.name_postion || "ไม่ระบุ").substring(0, 25) +
            ((p.name_postion || "").length > 25 ? "..." : "")
        );
        this.positionChartSeries = [
          {
            name: "จำนวนคน",
            data: positions.map(p => {
              const val = parseInt(p.count);
              return isNaN(val) ? 0 : val;
            })
          }
        ];
      }

      // Update Education Chart - แก้ปัญหาการแสดงผล
      // Update Education Chart
      const education = statsData.education_levels || [];
      if (education.length > 0) {
        // 1. สร้างตัวแปรเก็บข้อมูลแบบ Grouping เพื่อรวมยอด
        const educationGroups = {};

        education.forEach(e => {
          let label = (e.level_edd || "ไม่ระบุ").trim();
          const count = parseInt(e.count) || 0;

          // (Optional) ส่วนนี้คือการแก้คำผิดแบบ Hardcode
          // ถ้าคุณแก้ที่ Database ได้ แนะนำให้แก้ที่ต้นทางดีที่สุดครับ
          // แต่ถ้าแก้ไม่ได้ ให้ใช้ Logic ทำความสะอาดข้อมูลตรงนี้:
          if (
            label.startsWith("ปริญญาตรี") ||
            label.startsWith("ปริญา") ||
            label.startsWith("ปริย")
          ) {
            label = "ปริญญาตรีหรือเทียบเท่า";
          } else if (
            label.startsWith("ประกาศนียบัตรวิชาชีพชั้นสูง") ||
            label.includes("ปวส")
          ) {
            label = "ปวส.";
          } else if (
            label.startsWith("ประกาศนียบัตรวิชาชีพ") ||
            label.includes("ปวช")
          ) {
            label = "ปวช.";
          }

          // ตัดคำถ้ายาวเกินไป (เพื่อให้แสดงผลสวย)
          if (label.length > 20) {
            label = label.substring(0, 20) + "...";
          }

          // รวมผลลัพธ์ (Sum)
          if (educationGroups[label]) {
            educationGroups[label] += count;
          } else {
            educationGroups[label] = count;
          }
        });

        // 2. แปลง Object กลับเป็น Array สำหรับกราฟ
        // เรียงลำดับจากมากไปน้อย (เพื่อให้กราฟสวยงาม)
        const sortedEntries = Object.entries(educationGroups).sort(
          ([, a], [, b]) => b - a
        ); // เรียงตามจำนวนคน

        const newCategories = sortedEntries.map(([label]) => label);
        const newData = sortedEntries.map(([, count]) => count);

        // 3. อัปเดตข้อมูลเข้ากราฟ
        // วิธีที่ดีที่สุดคือการ Clone Object เพื่อกระตุ้น Reactivity ของ Vue
        this.educationChartOptions = {
          ...this.educationChartOptions,
          xaxis: {
            ...this.educationChartOptions.xaxis,
            categories: newCategories
          }
        };

        this.educationChartSeries = [
          {
            name: "จำนวนคน",
            data: newData
          }
        ];

        // บังคับ Re-render (ที่คุณทำอยู่แล้วใช้ได้ครับ)
        this.educationChartKey += 1;
      }
    },

    async onFilterChange() {
      this.loading = true;
      try {
        await this.loadEmployeeData();
        await this.loadStats();
      } finally {
        this.loading = false;
      }
    },

    async government_employeeQuerynull() {
      this.college_search = "";
      this.province_search = "";
      this.search = "";
      await this.onFilterChange();
    },

    async government_employeeQueryAll() {
      this.college_search = "";
      this.province_search = "";
      this.search = "";
      this.loading = true;
      try {
        const result = await this.$http.post("government_employee_news.php", {
          ApiKey: this.ApiKey
        });
        this.government_employees = result.data.data || [];
        await this.loadStats();
      } finally {
        this.loading = false;
      }
    },

    async idPosition_search() {
      if (!this.search) return;
      this.loading = true;
      try {
        const result = await this.$http.post("government_employee_news.php", {
          ApiKey: this.ApiKey,
          id_position: this.search
        });
        this.government_employees = result.data.data || [];
      } finally {
        this.loading = false;
      }
    },

    government_employeeAdd() {
      this.addgovernment_employee = {};
      this.addgovernment_employeedialog = true;
    },

    async addgovernment_employeeSubmit() {
      if (!this.$refs.addgovernment_employeeform.validate()) return;

      this.loading = true;
      try {
        this.addgovernment_employee.ApiKey = this.ApiKey;
        let result = await this.$http.post(
          "government_employee.insert.php",
          this.addgovernment_employee
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "ดำเนินการบันทึกข้อมูลเรียบร้อย",
            showConfirmButton: false,
            timer: 1500
          });

          let userSession = JSON.parse(sessionStorage.getItem("user")) || {};
          this.data_syslog.ApiKey = this.ApiKey;
          this.data_syslog.user_account = userSession.user_name || "";
          this.data_syslog.event_log = "insert";
          this.data_syslog.page_log = "government_employee";
          this.data_syslog.table_log = "government_employee";
          this.data_syslog.detail_log =
            this.addgovernment_employee.id_position +
            ":" +
            this.addgovernment_employee.college_code;
          this.data_syslog.date_times = this.date_today_log;

          await this.$http.post("data_syslog.insert.php", this.data_syslog);
          await this.onFilterChange();
          this.addgovernment_employeedialog = false;
        }
      } catch (error) {
        console.error("Error:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: error.message,
          timer: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    async government_employeeEdit(id_no) {
      this.loading = true;
      try {
        let result = await this.$http.post("government_employee_news.php", {
          ApiKey: this.ApiKey,
          id_no: id_no
        });
        this.editgovernment_employee = result.data.data || {};
        await this.editgovernment_employeeFullname();
        this.editgovernment_employeedialog = true;
      } catch (error) {
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },

    async editgovernment_employeeFullname() {
      const title =
        this.editgovernment_employee.id_card +
        " : " +
        this.editgovernment_employee.title_s +
        this.editgovernment_employee.frist_name +
        " " +
        this.editgovernment_employee.last_name;
      this.fullname = title;
    },

    async editgovernment_employeeSubmit() {
      if (!this.$refs.editgovernment_employeeform.validate()) return;

      this.loading = true;
      try {
        this.editgovernment_employee.ApiKey = this.ApiKey;
        let result = await this.$http.post(
          "government_employee.update.php",
          this.editgovernment_employee
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "ดำเนินการแก้ไขข้อมูลเรียบร้อย",
            showConfirmButton: false,
            timer: 1500
          });

          let userSession = JSON.parse(sessionStorage.getItem("user")) || {};
          this.data_syslog.ApiKey = this.ApiKey;
          this.data_syslog.user_account = userSession.user_name || "";
          this.data_syslog.event_log = "update";
          this.data_syslog.page_log = "government_employee";
          this.data_syslog.table_log = "government_employee";
          this.data_syslog.detail_log =
            this.editgovernment_employee.id_position +
            " : " +
            this.editgovernment_employee.college_code;
          this.data_syslog.date_times = this.date_today_log;

          await this.$http.post("data_syslog.insert.php", this.data_syslog);
          await this.onFilterChange();
          this.editgovernment_employeedialog = false;
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },

    async clearIdcard() {
      const text =
        "เลขที่ตำแหน่ง : " +
        this.editgovernment_employee.id_position +
        " โดย : " +
        this.editgovernment_employee.id_card;

      this.$swal
        .fire({
          title: "ต้องการลบข้อมูลหรือไม่?",
          text: text,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "ยืนยัน",
          cancelButtonText: "ยกเลิก"
        })
        .then(async result => {
          if (result.isConfirmed) {
            this.loading = true;
            try {
              this.delIppostion = { ...this.editgovernment_employee };
              this.delIppostion.ApiKey = this.ApiKey;
              this.delIppostion.id_card = 0;

              let resultins = await this.$http.post(
                "government_employee.update.php",
                this.delIppostion
              );

              if (resultins.data.status === true) {
                Swal.fire({
                  icon: "success",
                  title: "ดำเนินการลบข้อมูลเรียบร้อย",
                  showConfirmButton: false,
                  timer: 1500
                });
                await this.onFilterChange();
                this.editgovernment_employee.id_card = 0;
              }
            } catch (error) {
              console.error("Error:", error);
            } finally {
              this.loading = false;
            }
          }
        });
    },

    government_employeeDelete(id_no) {
      this.$http
        .post("government_employee_news.php", {
          ApiKey: this.ApiKey,
          id_no: id_no
        })
        .then(result => {
          this.editgovernment_employee = result.data.data || {};
          this.deleteConfirm = "";
          this.deletegovernment_employeedialog = true;
        });
    },

    async deletegovernment_employeeSubmit() {
      if (!this.$refs.deletegovernment_employeeform.validate()) return;
      if (this.deleteConfirm !== "ยืนยันการลบ") return;

      this.loading = true;
      try {
        this.editgovernment_employee.ApiKey = this.ApiKey;
        let result = await this.$http.post(
          "government_employee.delete.php",
          this.editgovernment_employee
        );

        if (result.data.status === true) {
          Swal.fire({
            icon: "success",
            title: "ดำเนินการลบข้อมูลเรียบร้อย",
            showConfirmButton: false,
            timer: 1500
          });

          let userSession = JSON.parse(sessionStorage.getItem("user")) || {};
          this.data_syslog.ApiKey = this.ApiKey;
          this.data_syslog.user_account = userSession.user_name || "";
          this.data_syslog.event_log = "delete";
          this.data_syslog.page_log = "government_employee";
          this.data_syslog.table_log = "government_employee";
          this.data_syslog.detail_log =
            this.editgovernment_employee.id_position +
            " : " +
            this.editgovernment_employee.college_code;
          this.data_syslog.date_times = this.date_today_log;

          await this.$http.post("data_syslog.insert.php", this.data_syslog);
          await this.onFilterChange();
          this.deletegovernment_employeedialog = false;
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },

    exportToCSV() {
      const headers = [
        "เลขที่ตำแหน่ง",
        "ชื่อ-สกุล",
        "ตำแหน่ง",
        "วิทยาลัย",
        "เพศ",
        "เงินเดือน",
        "จังหวัด"
      ];
      const data = this.government_employees.map(emp => [
        emp.id_position,
        (emp.title_s || "") +
          (emp.frist_name || "") +
          " " +
          (emp.last_name || ""),
        emp.name_postion,
        emp.user_name,
        emp.sex,
        emp.salary_s,
        emp.province_name
      ]);

      const csv = [headers, ...data]
        .map(row => row.map(cell => `"${cell || ""}"`).join(","))
        .join("\n");

      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;"
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download =
        "government_employee_" +
        new Date().toISOString().split("T")[0] +
        ".csv";
      link.click();
    },
    exportSummaryToCSV() {
      const headers = [
        "รหัสสถานศึกษา",
        "สถานศึกษา",
        "จำนวนคน",
        "เงินเดือนรวม",
        "เงินเดือนเฉลี่ย",
        "ชาย",
        "หญิง"
      ];
      const data = this.collegeSummary.map(emp => [
        emp.user_code,
        emp.user_firstname,
        emp.total_employees,
        emp.total_salary,
        emp.avg_salary,
        emp.male_count,
        emp.female_count
      ]);

      const csv = [headers, ...data]
        .map(row => row.map(cell => `"${cell || ""}"`).join(","))
        .join("\n");

      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;"
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download =
        "รายงานสรุปอัตรากำลังสถานศึกษา_" +
        new Date().toISOString().split("T")[0] +
        ".csv";
      link.click();
    }
  }
};
</script>

<style scoped>
.government-employee-container {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

/* Header Gradient */
.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.4) !important;
}

.bg-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-blue {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
}

.bg-gradient-green {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

.bg-gradient-orange {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.bg-gradient-purple {
  background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%);
}

/* Summary Cards Animation */
.summary-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: white;
  padding: 16px;
}

.summary-card-hover:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.summary-card.delay-0 {
  animation: slideInUp 0.5s ease-out 0.1s both;
}

.summary-card.delay-1 {
  animation: slideInUp 0.5s ease-out 0.2s both;
}

.summary-card.delay-2 {
  animation: slideInUp 0.5s ease-out 0.3s both;
}

.summary-card.delay-3 {
  animation: slideInUp 0.5s ease-out 0.4s both;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stat Cards */
.stat-card {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  background: white;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

/* Chart Cards */
.chart-card {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: white;
  overflow: hidden;
}

.chart-container {
  position: relative;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-wrapper {
  width: 100%;
  height: 350px;
}

/* Table Hover Effect */
.table-hover {
  border-radius: 12px !important;
}

.table-hover ::v-deep tbody tr:hover {
  background-color: #f5f5f5;
  transition: background-color 0.2s ease;
}

/* Color Classes */
.bg-primary {
  background-color: #1976d2 !important;
}

.bg-secondary {
  background-color: #424242 !important;
}

.bg-success {
  background-color: #43a047 !important;
}

.bg-error {
  background-color: #e53935 !important;
}

.bg-gray {
  background-color: #757575 !important;
}

.bg-light-gray {
  background-color: #f5f5f5 !important;
}

.white--text {
  color: white !important;
}

.gray--text {
  color: #999 !important;
}

.text-primary {
  color: #1976d2 !important;
}

.text-success {
  color: #43a047 !important;
}

.text-info {
  color: #00bcd4 !important;
}

.text-warning {
  color: #ff9800 !important;
}

.text-error {
  color: #d32f2f !important;
}

.text-secondary {
  color: #666 !important;
}

/* Text Sizing */

.font-weight-bold {
  font-weight: 600 !important;
}

.opacity-8 {
  opacity: 0.8;
}

.sticky-top {
  position: sticky !important;
  top: 0;
  z-index: 10;
}

/* Spacing */
.mb-4 {
  margin-bottom: 1.5rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

.mb-2 {
  margin-bottom: 0.5rem !important;
}

.my-2 {
  margin-top: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

.my-3 {
  margin-top: 1rem !important;
  margin-bottom: 1rem !important;
}

.mr-2 {
  margin-right: 0.5rem !important;
}

.mr-1 {
  margin-right: 0.25rem !important;
}

.ml-2 {
  margin-left: 0.5rem !important;
}

.mt-2 {
  margin-top: 0.5rem !important;
}

.mt-4 {
  margin-top: 1.5rem !important;
}

.pa-4 {
  padding: 1.5rem !important;
}

.py-4 {
  padding-top: 1.5rem !important;
  padding-bottom: 1.5rem !important;
}

.py-8 {
  padding-top: 2rem !important;
  padding-bottom: 2rem !important;
}

.h-100 {
  height: 100%;
}

/* Button Group */
.button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* Elevation */
.elevation-1 {
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
}

.elevation-2 {
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
}

.elevation-3 {
  box-shadow: 0 3px 3px -2px rgba(0, 0, 0, 0.2), 0 3px 4px 0 rgba(0, 0, 0, 0.14),
    0 1px 8px 0 rgba(0, 0, 0, 0.12);
}

/* Responsive */
@media (max-width: 768px) {
  .government-employee-container {
    padding: 12px;
  }

  .header-card {
    margin-bottom: 16px;
  }

  .button-group {
    flex-direction: column;
  }

  .button-group button {
    width: 100%;
  }

  .chart-container {
    min-height: 300px;
  }

  .chart-wrapper {
    height: 300px;
  }
}
</style>
